import routes from 'app.routes';
import DashBoard from 'modules/v2/dashboard/DashBoard';
import Login from 'modules/v2/auth/Login';
import AuthRedirectPageContent from 'modules/auth/AuthRedirectPageContent';
import AuthLayout from 'components/v2/layout/AuthLayout';
import PageLayout from 'modules/common/PageLayout';
import Page from 'modules/common/Page';
import ActiveDirectory from 'modules/v2/activedirectory/ActiveDirectory';
import OrderDetailInCase from 'modules/v2/cases/detail/OrderDetailInCase';
import Presentation from 'modules/v2/presentation/Presentation';
import type { AgnosticIndexRouteObject } from '@remix-run/router';
import NotFoundPage from 'components/v2/shared/NotFoundPage';
import AcceptInvitation from 'modules/v2/guest/AcceptInvitation';
import Booking from 'modules/v2/guest/Booking';
import Interview from 'modules/v2/guest/Interview';
import Questionnaire from 'modules/v2/guest/Questionnaire';
import AccountSettings from 'modules/v2/accountsetting/overview/AccountSettings';
import { lazyWithReload } from 'helpers/lazyWithReload';
import CreateOrderRedirectFlow from 'modules/v2/order/neworder/CreateOrderRedirectFlow';
import CreateAdsCampaignOrder from 'modules/v2/order/ads/CreateAdsCampaignOrder';
import CreateNewOrder from 'modules/v2/order/neworder/CreateNewOrder';

const Campaigns = lazyWithReload(() => import('modules/v2/ads/overview/Campaigns'));
const Tasks = lazyWithReload(() => import('modules/v2/tasks/overview/Tasks'));
const CaseDetail = lazyWithReload(() => import('modules/v2/cases/detail/CaseDetail'));
const Cases = lazyWithReload(() => import('modules/v2/cases/overview/Cases'));

export type RouteConfig = {
  path: string;
  element: React.ReactNode;
  key?: string;
  loader?: AgnosticIndexRouteObject['loader'];
};

export const routesConfig: RouteConfig[] = [
  {
    // Authentication
    path: routes.v2.login,
    element: (
      <PageLayout.Provider>
        <Page layout="side" titleKey="navigation.pageTitles.login">
          <Login />
        </Page>
      </PageLayout.Provider>
    ),
    key: 'login',
  },
  {
    path: routes.v2.authRedirect,
    element: (
      <PageLayout.Provider>
        <Page layout="basic" titleKey="navigation.pageTitles.auth">
          <AuthRedirectPageContent />
        </Page>
      </PageLayout.Provider>
    ),
    key: 'authRedirect',
  },
  {
    path: routes.v2.activeDirectory,
    element: (
      <PageLayout.Provider>
        <Page layout="side" guard titleKey="navigation.pageTitles.adLogin">
          <ActiveDirectory />
        </Page>
      </PageLayout.Provider>
    ),
    key: 'activeDirectory',
  },

  // Services
  {
    path: routes.v2.dashboard,
    element: (
      <AuthLayout guard titleKey="navigation.mainMenu.dashboard">
        <DashBoard />
      </AuthLayout>
    ),
    key: 'dashboard',
  },
  {
    path: routes.v2.tasks.overview,
    element: (
      <AuthLayout guard titleKey="navigation.mainMenu.tasks">
        <Tasks />
      </AuthLayout>
    ),
    key: 'tasks',
  },
  {
    path: routes.v2.cases.overview,
    element: (
      <AuthLayout guard titleKey="navigation.mainMenu.cases">
        <Cases />
      </AuthLayout>
    ),
    key: 'cases',
  },
  {
    path: routes.v2.campaigns.overview,
    element: (
      <AuthLayout guard titleKey="navigation.mainMenu.campaigns">
        <Campaigns />
      </AuthLayout>
    ),
    key: 'campaigns',
  },
  {
    path: routes.v2.order.newOrder,
    element: (
      <AuthLayout guard titleKey="navigation.pageTitles.newOrder">
        <CreateNewOrder />
      </AuthLayout>
    ),
    key: 'NewOrder',
  },
  {
    path: routes.v2.order.adsOrder,
    element: (
      <AuthLayout guard titleKey="navigation.pageTitles.newOrder">
        <CreateAdsCampaignOrder />
      </AuthLayout>
    ),
    key: 'CreateNewAdsOrder',
  },
  {
    path: routes.redirectNewOrderForCase(),
    element: (
      <AuthLayout guard titleKey="navigation.pageTitles.newOrder">
        <CreateOrderRedirectFlow />
      </AuthLayout>
    ),
    key: 'CreateNewOrderRedirectFlow',
  },
  {
    path: routes.v2.accountSettings.overview,
    element: (
      <AuthLayout guard titleKey="navigation.secondaryMenu.accountSetting">
        <AccountSettings />
      </AuthLayout>
    ),
    key: 'AccountSettings',
  },
  {
    path: routes.presentation.toString(),
    element: (
      <AuthLayout guard titleKey="materials.pages.presentation.og.title">
        <Presentation />
      </AuthLayout>
    ),
    key: 'presentation',
  },
  {
    path: routes.acceptInvite().toString(),
    element: <AcceptInvitation />,
    key: 'accept-invite',
  },
  {
    path: routes.v2.booking,
    element: <Booking />,
    key: 'booking',
  },
  {
    path: routes.v2.interview,
    element: <Interview />,
    key: 'interview',
  },
  {
    path: routes.v2.questionnaire,
    element: <Questionnaire />,
    key: 'questionnaire',
  },
  {
    path: '*',
    element: <NotFoundPage />,
    key: 'notfound',
  },

  //details:
  {
    path: routes.v2.cases.detail,
    element: (
      <AuthLayout guard titleKey="navigation.mainMenu.cases">
        <CaseDetail />
      </AuthLayout>
    ),
    key: 'case-detail',
  },
  {
    // This route is used for redirecting to match with URL structure in old webshop
    path: routes.v2.cases.detailForRedirect,
    element: (
      <AuthLayout guard titleKey="navigation.mainMenu.cases">
        <CaseDetail />
      </AuthLayout>
    ),
    key: 'case-detail-for-redirect',
  },
  {
    path: routes.v2.cases.orderDetail,
    element: (
      <AuthLayout guard titleKey="navigation.mainMenu.cases">
        <OrderDetailInCase />
      </AuthLayout>
    ),
    key: 'case-order-detail',
  },
  // This route is used for redirecting to match with URL structure in old webshop
  {
    path: routes.cases.viewOrder.toString(),
    element: (
      <AuthLayout guard titleKey="navigation.mainMenu.cases">
        <OrderDetailInCase />
      </AuthLayout>
    ),
    key: 'case-order-detail-redirect-url',
  },
];

export const routesConfigWithoutLayout: RouteConfig[] = [];
